/**
 * Phase-gates slice (PX-W6.3, TASK-EDD04F2F) — the onboarding gates, the
 * engagement phase view, and the step-04 config snapshots of one open
 * workspace.
 *
 * Decision model: `POST …/decision` returns the REFRESHED gate view, so the
 * fulfilled case swaps that one row in `gates` immediately (no list refetch)
 * AND refetches the phase view, because an approval can move the engagement
 * (flow.04 → step-06 kickoff readiness, ageing resets). Refusals land as
 * keyed 400s in `error` with their `gateRef` — the dialog renders the
 * server's message inline and never claims an audit trail (INV-7: only
 * ACTION_STEP_BLOCKED writes one, server-side).
 */

import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'

import { isApiError, toApiError } from '@/services'
import {
  fetchFailed,
  fetchStarted,
  fetchSucceeded,
  initialFetchCache,
  invalidate,
  shouldFetch,
  type FetchCache,
} from '@/store/fetchCache'
import { projectsBusFrame } from '@/features/projects/slices/projectsSlice'
import { phaseGateService } from '../services/phaseGateService.http'
import type {
  ConfigPayloadView,
  ConfigSnapshotRow,
  GateDecisionInput,
  PhaseGateView,
  ProjectPhaseView,
} from '../types'

/** A keyed refusal — `gateRef` names the gate when the error is a decision/submit refusal. */
export interface GatesError {
  gateRef?: string
  status: number
  code?: string
  message: string
}

export interface PhaseGatesState extends FetchCache {
  projectId: string | null
  gates: PhaseGateView[]
  phase: ProjectPhaseView | null
  snapshots: ConfigSnapshotRow[]
  /** The project the snapshot list was loaded for — the once-per-mount guard. */
  snapshotsLoadedKey: string | null
  /** Bumped when a flow.04 approval re-arms the snapshot cache — the panel refetches. */
  snapshotsNonce: number
  payload: ConfigPayloadView | null
  /** gateRef of the decision currently in flight (drives the dialog's spinner). */
  deciding: string | null
  error: GatesError | null
  /** Bumped by bus frames for this project — the panels refetch on movement. */
  invalidationNonce: number
}

const initialState: PhaseGatesState = {
  projectId: null,
  gates: [],
  phase: null,
  snapshots: [],
  snapshotsLoadedKey: null,
  snapshotsNonce: 0,
  payload: null,
  deciding: null,
  error: null,
  invalidationNonce: 0,
  ...initialFetchCache,
}

interface ProjectArg {
  orgId: string
  projectId: string
}

/** Map any thrown value to a gates-shaped error payload. */
const toGatesError = (error: unknown, gateRef?: string): GatesError => {
  const apiError = isApiError(error) ? error : toApiError(error)
  return {
    gateRef,
    status: apiError.status,
    code: apiError.backendKey,
    message: apiError.message,
  }
}

// ---------------------------------------------------------------------------
// Thunks
// ---------------------------------------------------------------------------

/** Mount: gates + phase view in one thunk (the overview renders both). */
export const openGates = createAsyncThunk<
  { gates: PhaseGateView[]; phase: ProjectPhaseView },
  ProjectArg,
  { rejectValue: GatesError }
>(
  'phaseGates/open',
  async ({ orgId, projectId }, { rejectWithValue }) => {
    try {
      const [gates, phase] = await Promise.all([
        phaseGateService.listGates(orgId, projectId),
        phaseGateService.getPhase(orgId, projectId),
      ])
      return { gates, phase }
    } catch (error) {
      return rejectWithValue(toGatesError(error))
    }
  },
  {
    condition: ({ projectId }, { getState }) => {
      const { phaseGates } = getState() as { phaseGates: PhaseGatesState }
      return shouldFetch(phaseGates, projectId)
    },
  }
)

/** Refetch the phase view only (after a decision, or on demand). */
export const fetchPhase = createAsyncThunk<
  ProjectPhaseView,
  ProjectArg,
  { rejectValue: GatesError }
>(
  'phaseGates/fetchPhase',
  async ({ orgId, projectId }, { rejectWithValue }) => {
    try {
      return await phaseGateService.getPhase(orgId, projectId)
    } catch (error) {
      return rejectWithValue(toGatesError(error))
    }
  }
)

/** Record a decision — swaps the returned gate row in, then refetches phase. */
export const decideGate = createAsyncThunk<
  PhaseGateView,
  ProjectArg & { gateRef: string; input: GateDecisionInput },
  { rejectValue: GatesError }
>(
  'phaseGates/decide',
  async (
    { orgId, projectId, gateRef, input },
    { dispatch, rejectWithValue }
  ) => {
    try {
      const gate = await phaseGateService.decide(
        orgId,
        projectId,
        gateRef,
        input
      )
      // The approval can change the engagement's phase facts — refresh them.
      await dispatch(fetchPhase({ orgId, projectId }))
      return gate
    } catch (error) {
      return rejectWithValue(toGatesError(error, gateRef))
    }
  }
)

/** Record the flow.03 submit act — same swap-in model as a decision. */
export const submitGate = createAsyncThunk<
  PhaseGateView,
  ProjectArg & { gateRef: string },
  { rejectValue: GatesError }
>(
  'phaseGates/submit',
  async ({ orgId, projectId, gateRef }, { dispatch, rejectWithValue }) => {
    try {
      const gate = await phaseGateService.submitGate(orgId, projectId, gateRef)
      await dispatch(fetchPhase({ orgId, projectId }))
      return gate
    } catch (error) {
      return rejectWithValue(toGatesError(error, gateRef))
    }
  }
)

/**
 * The kickoff act: the FIRST transition to `discover` (flow step 06). A keyed
 * 400 (non-sequential, already entered) lands in `error` with its code.
 */
export const startEngagement = createAsyncThunk<
  ProjectPhaseView,
  ProjectArg,
  { rejectValue: GatesError }
>(
  'phaseGates/startEngagement',
  async ({ orgId, projectId }, { rejectWithValue }) => {
    try {
      return await phaseGateService.transitionPhase(orgId, projectId, {
        toPhase: 'discover',
      })
    } catch (error) {
      return rejectWithValue(toGatesError(error))
    }
  }
)

/** Load the flow.04 review payload (live pre-approval, frozen after). */
export const loadPayload = createAsyncThunk<
  ConfigPayloadView,
  ProjectArg & { gateRef: string; version?: number },
  { rejectValue: GatesError }
>(
  'phaseGates/loadPayload',
  async ({ orgId, projectId, gateRef, version }, { rejectWithValue }) => {
    try {
      return await phaseGateService.getPayload(
        orgId,
        projectId,
        gateRef,
        version
      )
    } catch (error) {
      return rejectWithValue(toGatesError(error, gateRef))
    }
  }
)

/**
 * List the locked config versions. Guards itself: once loaded for the
 * mounted project, the panel's own mount effect will not re-fetch — a
 * flow.04 approval re-arms the guard (`snapshotsLoadedKey = null`) and bumps
 * `snapshotsNonce`, which is the panel's live-refresh signal.
 */
export const loadSnapshots = createAsyncThunk<
  ConfigSnapshotRow[],
  ProjectArg,
  { rejectValue: GatesError }
>(
  'phaseGates/loadSnapshots',
  async ({ orgId, projectId }, { rejectWithValue }) => {
    try {
      return await phaseGateService.listSnapshots(orgId, projectId)
    } catch (error) {
      return rejectWithValue(toGatesError(error))
    }
  },
  {
    // No `state` generic (the pre-typed AppDispatch rejects narrower thunks);
    // getState is cast locally — the sessionSlice precedent.
    condition: ({ projectId }, { getState }) => {
      const { phaseGates } = getState() as { phaseGates: PhaseGatesState }
      return (
        phaseGates.projectId !== projectId ||
        phaseGates.snapshotsLoadedKey !== projectId
      )
    },
  }
)

// ---------------------------------------------------------------------------
// Slice
// ---------------------------------------------------------------------------

/** Replace (or append) one gate row by `ref`. */
const swapGate = (state: PhaseGatesState, gate: PhaseGateView): void => {
  const index = state.gates.findIndex((g) => g.ref === gate.ref)
  if (index === -1) {
    state.gates.push(gate)
  } else {
    state.gates[index] = gate
  }
}

export const phaseGatesSlice = createSlice({
  name: 'phaseGates',
  initialState,
  reducers: {
    /** Leave the workspace — resets everything. */
    gatesClosed(state) {
      state.projectId = null
      state.gates = []
      state.phase = null
      state.snapshots = []
      state.snapshotsLoadedKey = null
      state.snapshotsNonce = 0
      state.payload = null
      state.deciding = null
      state.error = null
      state.invalidationNonce = 0
      invalidate(state)
    },
    /** Dismiss a refusal (the dialog's inline error). */
    gatesErrorDismissed(state) {
      state.error = null
    },
    /** Explicit retry re-arms a failed primary gate read. */
    gatesRefetchArmed(state) {
      invalidate(state)
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(openGates.pending, (state, action) => {
        if (state.projectId !== action.meta.arg.projectId) {
          state.gates = []
          state.phase = null
          state.snapshots = []
          state.payload = null
          state.error = null
        }
        fetchStarted(state)
        state.projectId = action.meta.arg.projectId
        state.error = null
      })
      .addCase(openGates.fulfilled, (state, action) => {
        // Normalised: a malformed payload (e.g. a service seam resolving
        // undefined in tests) must never write undefined into list state —
        // downstream selectors and panels destructure without guards.
        state.gates = action.payload.gates ?? []
        state.phase = action.payload.phase ?? null
        fetchSucceeded(state, action.meta.arg.projectId)
      })
      .addCase(openGates.rejected, (state, action) => {
        fetchFailed(state)
        state.error = action.payload ?? null
      })
      .addCase(fetchPhase.fulfilled, (state, action) => {
        state.phase = action.payload
      })
      .addCase(decideGate.pending, (state, action) => {
        state.deciding = action.meta.arg.gateRef
        state.error = null
      })
      .addCase(decideGate.fulfilled, (state, action) => {
        state.deciding = null
        swapGate(state, action.payload)
        // An approved flow.04 locks a NEW config version. Re-arm the snapshot
        // guard and bump the nonce — the mounted ConfigSnapshotPanel refetches
        // and shows the newly frozen version immediately (its own mount effect,
        // not a refetch performed here).
        if (action.meta.arg.gateRef === 'flow.04') {
          state.snapshotsLoadedKey = null
          state.snapshotsNonce += 1
        }
      })
      .addCase(decideGate.rejected, (state, action) => {
        state.deciding = null
        state.error = action.payload ?? null
      })
      .addCase(submitGate.fulfilled, (state, action) => {
        swapGate(state, action.payload)
      })
      .addCase(submitGate.rejected, (state, action) => {
        state.error = action.payload ?? null
      })
      .addCase(startEngagement.fulfilled, (state, action) => {
        state.phase = action.payload
      })
      .addCase(startEngagement.rejected, (state, action) => {
        state.error = action.payload ?? null
      })
      .addCase(loadPayload.fulfilled, (state, action) => {
        state.payload = action.payload
      })
      .addCase(loadSnapshots.fulfilled, (state, action) => {
        state.snapshots = action.payload
        state.snapshotsLoadedKey = action.meta.arg.projectId
      })
      .addCase(projectsBusFrame, (state, action) => {
        // A roster or project write (bus `project.updated`) can move gate
        // criteria (lead evidence, spend caps) — refetch-on-invalidate, never
        // a client-side re-derivation of a gate.
        const frame = action.payload
        if (
          frame.event === 'project.updated' &&
          frame.project?.id === state.projectId
        ) {
          state.invalidationNonce += 1
          invalidate(state)
        }
      })
  },
})

export const { gatesClosed, gatesErrorDismissed, gatesRefetchArmed } =
  phaseGatesSlice.actions

/* ── Selectors ──────────────────────────────────────────────────────────── */

interface WithPhaseGates {
  phaseGates: PhaseGatesState
}

export const selectGates = (s: WithPhaseGates) => s.phaseGates.gates
export const selectPhaseView = (s: WithPhaseGates) => s.phaseGates.phase
export const selectSnapshots = (s: WithPhaseGates) => s.phaseGates.snapshots
/** The snapshot-cache re-arm signal — the panel refetches when it moves. */
export const selectSnapshotsNonce = (s: WithPhaseGates) =>
  s.phaseGates.snapshotsNonce
export const selectConfigPayload = (s: WithPhaseGates) => s.phaseGates.payload
export const selectDecidingGate = (s: WithPhaseGates) => s.phaseGates.deciding
export const selectGatesError = (s: WithPhaseGates) => s.phaseGates.error
export const selectGatesLoading = (s: WithPhaseGates) => s.phaseGates.loading
export const selectGatesNonce = (s: WithPhaseGates) =>
  s.phaseGates.invalidationNonce
